import {Controller} from "@hotwired/stimulus"

// Connects to data-controller="password-requirements"
//
// Live-validates a password field against the exact rules the server
// enforces (User::PASSWORD_FORMAT + length validation in app/models/user.rb,
// plus has_secure_password's 72-character cap). Each requirement in the hint
// list gets a met/unmet state as the user types, and submission is blocked
// client-side while any requirement is unmet so users don't bounce off
// server-side errors.
export default class extends Controller {
  static targets = ["field", "rule"]

  // Keep in sync with User::PASSWORD_FORMAT (app/models/user.rb).
  // Ruby's [[:^alnum:]] (any non-alphanumeric, unicode-aware) maps to
  // /[^\p{L}\p{N}]/u in JavaScript.
  rules = {
    length: (value) => value.length >= 8 && value.length <= 72,
    letter: (value) => /[A-Za-z]/.test(value),
    number: (value) => /\d/.test(value),
    symbol: (value) => /[^\p{L}\p{N}]/u.test(value)
  }

  connect() {
    this.form = this.element.closest("form")
    if (this.form) {
      this.boundCheckSubmit = this.checkSubmit.bind(this)
      this.form.addEventListener("submit", this.boundCheckSubmit)
    }
    this.validate()
  }

  disconnect() {
    if (this.form) {
      this.form.removeEventListener("submit", this.boundCheckSubmit)
    }
  }

  validate() {
    const value = this.fieldTarget.value

    this.ruleTargets.forEach((rule) => {
      const check = this.rules[rule.dataset.rule]
      const met = check ? check(value) : false
      rule.classList.toggle("met", value.length > 0 && met)
      rule.classList.toggle("unmet", value.length > 0 && !met)
    })

    // Once the user fixes things up, drop the error emphasis again.
    if (this.allRulesMet(value)) {
      this.element.classList.remove("show-requirement-errors")
    }
  }

  checkSubmit(event) {
    const value = this.fieldTarget.value

    // Let the browser's `required` validation handle the empty case.
    if (value.length === 0) return

    if (!this.allRulesMet(value)) {
      event.preventDefault()
      this.element.classList.add("show-requirement-errors")
      this.validate()
      this.fieldTarget.focus()
    }
  }

  allRulesMet(value) {
    return Object.values(this.rules).every((check) => check(value))
  }
}
